import { OnInit, OnDestroy } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { BroadcastService } from '@azure/msal-angular';
import { Subscription } from 'rxjs';
import { User } from 'msal';
import { IDiagnosticsService } from './services/interfaces';

/**
 * A base class for components that need to have awareness about whether or not
 * the current user is anonymous. This awareness is provided in the form of
 * 'loggedIn' boolean property.
 * The component subscribes to Microsoft Authentication Library (MSAL) broadcast
 * service, and sets the value of the 'loggedIn' property in accordance to received
 * messages, which child components can take advantage of.
 */
export abstract class LoginAwareComponent implements OnInit, OnDestroy {
  protected subscription: Subscription;
  protected userInfo: User;
  public loggedIn: boolean;
  public isIframe: boolean;

  constructor(
    protected diagnostics: IDiagnosticsService,
    protected broadcastService: BroadcastService,
    protected authService: MsalService) {
    this.userInfo = this.authService.getUser();
    this.loggedIn = (this.userInfo) ? true : false;
    this.isIframe = window !== window.parent && !window.opener;
  }

  /**
   * Attempts to silently acquire OpenID Connect authorization token.
   * @param scopes - an array of strings representing Azure permissions
   * to request with the token.
   */
  protected authorize(scopes: string[]): Promise<any> {
    return this.authService.acquireTokenSilent(scopes);
  }

  /**
   * Handles login success event. Default implementation sets 'loggedIn' property to true.
   * @param token - OpenID Connect identity token.
   */
  protected onLoginSuccess (token: any) {
    this.loggedIn = true;
    this.diagnostics.logDebug('Login success.');
  }

  /**
   * Called on login failure event.
   * @param payload - details of the failure, if applicable.
   */
  protected onLoginFailure (payload: any) {
    this.handleLoginFailure(payload);
  }

  /**
   * Colled on failure to acquire access token.
   * @param payload - details of the failure, if applicable.
   */
  protected onAcquireTokenFailure(payload: any): void {
    this.handleAcquireTokenFailure(payload);
  }

  /**
   * Called on acquiring access token successfully.
   * @param token - access token.
   */
  protected onAcquireTokenSuccess(token: any): void {
    this.diagnostics.logDebug(`Authorization success.`);
  }

  public ngOnInit(): void {
    this.subscription = this.broadcastService.subscribe(
      'msal:loginSuccess', (token) => { this.onLoginSuccess(token); });
    this.subscription.add(this.broadcastService.subscribe(
      'msal:loginFailure', (payload) => { this.onLoginFailure(payload); }));
    this.subscription.add(this.broadcastService.subscribe(
      'msal:acquireTokenSuccess', (token) => { this.onAcquireTokenSuccess (token); }));
    this.subscription.add(this.broadcastService.subscribe(
      'msal:acquireTokenFailure', (payload) => { this.onAcquireTokenFailure(payload); }));
  }

  /**
   * Default handler of login failure.
   * @param err - error to handle.
   */
  protected handleLoginFailure(err: any) {
    this.diagnostics.logError(`Failed to authenticate user. Details: ${JSON.stringify(err)}`);
  }

  /**
   * Default handler of failure to acquire access token.
   * @param err - error to handle.
   */
  protected handleAcquireTokenFailure(err: any) {
    this.diagnostics.logError(`Failed to acquire access token: ${ JSON.stringify(err) }`);
  }

  public ngOnDestroy(): void {
    this.broadcastService.getMSALSubject().next(1);
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
